import {
    UserProductEntitlements,
    ProductTypeIdentifiers,
    UserProductsAppStore,
    UserIntent,
    Organization,
    ContactDetails,
} from './DynamoTypes';

export type UTMParameters = {
    utm_campaign?: string;
    utm_content?: string;
    utm_medium?: string;
    utm_source?: string;
    utm_term?: string;
};

/**
 * Properties that are set on a Hubspot contact for a professional user
 */
export type ProfessionalUserContactProperties = {
    organization_id?: string;
    organization_role?: 'owner' | 'admin' | 'agent';
    organization_is_billing_admin?: 'true' | 'false';
};

/**
 * A subset of the properties of a Hubspot contact
 */
export type ContactProperties = {
    firstname: string;
    lastname: string;
    email: string;
    // for now, lifecycle stage will always be customer
    lifecyclestage: 'customer';
    has_deleted_app_user?: 'true' | 'false';
    phone?: string;
    registration_codes_used?: string;
    has_active_registration_code?: 'true' | 'false';
    has_connected_patient_portal?: 'true' | 'false';
    has_created_medical_profile?: 'true' | 'false';
    has_invited_collaborator?: 'true' | 'false';
    has_searched_patient_portals?: 'true' | 'false';
    last_app_login_datetime?: string;
    has_uploaded_file?: 'true' | 'false';
    has_added_note?: 'true' | 'false';
    last_added_note?: string;
    last_added_care_team_member?: string;
    last_chat_used_datetime?: string;
    last_generated_report_datetime?: string;
    last_generated_report_type?: string;
    last_generated_report_link_datetime?: string;
    // Subscription properties
    has_active_subscription?: 'true' | 'false';
    /**
     * A multi-checkbox property
     *
     * Values include: family-plan, individual-plan, and setup-and-training
     * The entry should be a semicolon-separated string of these values
     */
    subscription_products?: string;
    last_refreshed_portal_datetime?: string;
    subscription_duration?: 'yearly' | 'monthly' | 'lifetime';
    last_subscription_payment_datetime?: string;
    next_subscription_payment_datetime?: string;
    subscription_source_store?: 'stripe' | 'app_store' | 'play_store';
    /**
     * A multi-checkbox property
     *
     * Values include: manage-self, manage-family, collaborator-use, professional-use, collaborate-with-professional
     * The entry should be a semicolon-separated string of these values
     */
    user_intents?: string;
} & UTMParameters &
    ProfessionalUserContactProperties;

export type ModificationType =
    | 'registrationCodeApplied'
    | 'registrationCodeRemoved'
    | 'patientPortalConnected'
    | 'medicalProfileCreated'
    | 'collaboratorInvited'
    | 'patientPortalSearched'
    | 'addedNote'
    | 'lastAddedCareTeamMember'
    | 'uploadedFile'
    | 'appAccess'
    | 'reportGenerated'
    | 'refreshedPortal'
    | 'reportLinkGenerated'
    | 'subscriptionUpserted'
    | 'subscriptionCancelled'
    | 'userIntentsUpdated'
    | 'lastChatUsed'
    | 'accessedWithUTMParameters'
    | 'addedAsOrganizationUser'
    | 'removedAsOrganizationUser'
    | 'deletedUser'
    | 'updatedUserName';

type Empty = Record<string, never>;

export type ModificationPayloadMap = {
    registrationCodeApplied: {
        allUsedRegistrationCodes: string[];
    };
    registrationCodeRemoved: Empty;
    patientPortalConnected: Empty;
    medicalProfileCreated: Empty;
    collaboratorInvited: Empty;
    patientPortalSearched: Empty;
    addedNote: {
        addedNoteDateTime: string;
    };
    uploadedFile: Empty;
    appAccess: {
        appAccessDateTime: string;
    };
    subscriptionUpserted: {
        entitlements: UserProductEntitlements[];
        productTypeIdentifiers: ProductTypeIdentifiers[];
        sourceAppStore?: UserProductsAppStore | null;
        lastPaymentTimestamp?: number | null;
        nextPaymentTimestamp?: number | null;
    };
    userIntentsUpdated: {
        userIntents: UserIntent[];
    };
    lastAddedCareTeamMember: {
        lastCareTeamMemberAddedDateTime: string;
    };
    lastChatUsed: {
        lastChatUsedDateTime: string;
    };
    reportGenerated: {
        reportGeneratedDateTime: string;
        reportType: string;
    };
    reportLinkGenerated: {
        reportLinkGeneratedDateTime: string;
    };
    refreshedPortal: {
        refreshedPortalDateTime: string;
    };
    subscriptionCancelled: Empty;
    accessedWithUTMParameters: UTMParameters;
    addedAsOrganizationUser: {
        organizationId: string;
        /**
         * Optionally provide the `companyId` if it is known, otherwise it will be fetched from DynamoDB
         */
        companyId?: string;
        organizationRole: 'owner' | 'admin' | 'agent';
        organizationIsBillingAdmin?: boolean;
    };
    removedAsOrganizationUser: Empty;
    deletedUser: Empty;
    updatedUserName: {
        firstName: string;
        lastName: string;
    };
};

/**
 * A subset of the properties of a Hubspot company
 */
export type CompanyProperties = {
    name: string;
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    email?: string;
    phone?: string;
    website?: string;
    organization_id?: string;
    current_month_maximum_active_client_count?: string;
    last_month_maximum_active_client_count?: string;
};

export type ContactToCompanyPrimaryAssociation = {
    type: 'contactToCompanyPrimary';
    companyId: string;
};

export type Association = ContactToCompanyPrimaryAssociation;

export type CompanyModificationType =
    | 'organizationUpdated'
    | 'currentMonthMaximumMedicalProfileCountUpdated'
    | 'lastMonthMaximumMedicalProfileCountUpdated';

export type CompanyModificationPayloadMap = {
    organizationUpdated: {
        organizationItem: Organization;
        contactDetailsItem: ContactDetails;
    };
    currentMonthMaximumMedicalProfileCountUpdated: {
        maximumMedicalProfileCount: number;
    };
    lastMonthMaximumMedicalProfileCountUpdated: {
        maximumMedicalProfileCount: number;
    };
};
